import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { colorOf, fmtDate, urgency, daysUntil, EVENT_TYPES, STATUSES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Flame } from "lucide-react";
import { computeStreak } from "@/lib/streak";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "overdue", label: "Overdue" },
];

export default function AssignmentsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("due_date");

  useEffect(() => {
    (async () => {
      const [c, e] = await Promise.all([api.get("/courses"), api.get("/events")]);
      setCourses(c.data); setEvents(e.data); setLoading(false);
    })();
  }, []);

  const courseById = Object.fromEntries(courses.map((c) => [c.course_id, c]));

  const setStatus = async (ev, status) => {
    const prev = events;
    const updated = events.map((e) => e.event_id === ev.event_id
      ? { ...e, status, completed_at: status === "completed" ? new Date().toISOString() : null } : e);
    setEvents(updated);
    try {
      await api.patch(`/events/${ev.event_id}`, { status });
      if (status === "completed" && ev.status !== "completed") {
        const { streak } = computeStreak(updated);
        toast.success(
          streak > 1 ? `Nice work! 🔥 ${streak}-week streak — keep it going.` : "Nice work! One down. 🎉",
          { icon: <Flame className="h-4 w-4 text-amber-500" /> }
        );
      }
    } catch (e) {
      setEvents(prev);
      toast.error("Could not update status");
    }
  };

  let list = [...events];
  if (filter === "upcoming") list = list.filter((e) => e.status !== "completed" && (daysUntil(e.date) === null || daysUntil(e.date) >= 0));
  else if (filter === "completed") list = list.filter((e) => e.status === "completed");
  else if (filter === "overdue") list = list.filter((e) => e.status !== "completed" && daysUntil(e.date) !== null && daysUntil(e.date) < 0);

  if (sort === "due_date") list.sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));
  else if (sort === "course") list.sort((a, b) => (courseById[a.course_id]?.name || "").localeCompare(courseById[b.course_id]?.name || ""));
  else if (sort === "type") list.sort((a, b) => a.type.localeCompare(b.type));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Assignments</h1>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="assignments-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="due_date">Sort by due date</SelectItem>
            <SelectItem value="course">Sort by course</SelectItem>
            <SelectItem value="type">Sort by type</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
            }`}>{f.label}</button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="assignments-empty">
          {events.length === 0 ? (
            <>
              <p className="text-slate-600 mb-4">No assignments yet. Upload a syllabus to get started.</p>
              <Button onClick={() => navigate("/upload")} className="bg-blue-600 hover:bg-blue-700 rounded-lg"><Upload className="h-4 w-4 mr-1" /> Upload a Syllabus</Button>
            </>
          ) : (
            <p className="text-slate-500">{filter === "completed" ? "Complete your first task to see your progress." : "You're all caught up!"}</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((e) => {
            const c = courseById[e.course_id]; const col = colorOf(c?.color);
            const u = urgency(e.date, e.status);
            return (
              <div key={e.event_id} data-testid={`assignment-${e.event_id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <span className={`hidden sm:block h-2.5 w-2.5 rounded-full ${col.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{e.title}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${col.bg} ${col.text} capitalize`}>{e.type}</span>
                    {e.needs_review && <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] font-medium">Needs review</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{c?.code || c?.name} · Due {fmtDate(e.date)}</p>
                </div>
                <span className={`text-xs font-semibold ${u.color} shrink-0`}>{u.label}</span>
                <Select value={e.status} onValueChange={(v) => setStatus(e, v)}>
                  <SelectTrigger className="w-40 h-9" data-testid={`status-select-${e.event_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`status-opt-${s.key}`}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
